import { Link } from 'react-router-dom';
import Chip from '@mui/material/Chip';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import { PageHero } from '@/components/PageHero';
import { NotifyForm } from '@/components/NotifyForm';
import { Reveal } from '@/components/Reveal';
import { useSeo } from '@/seo/useSeo';

type Reality = { icon: SvgIconComponent; title: string; body: string };

const realities: Reality[] = [
  {
    icon: AccountBalanceOutlinedIcon,
    title: 'Multiple regulators can reach the same client at once',
    body: 'A community bank or credit union answers to federal and state banking regulators on top of the FTC Safeguards Rule under GLBA — an accounting or insurance client layers its own licensing expectations on top of that.',
  },
  {
    icon: CreditCardOutlinedIcon,
    title: 'PCI-DSS reaches further down than people assume',
    body: 'An insurance agency collecting premium payments or an accounting firm billing by card sits under PCI-DSS the same way a large retailer does, regardless of transaction volume.',
  },
  {
    icon: PolicyOutlinedIcon,
    title: 'Vendor oversight is a named requirement, not a nice-to-have',
    body: 'Financial regulators specifically ask clients how they oversee third-party vendors with system access — which means the MSP\'s own security posture becomes part of the client\'s own audit evidence.',
  },
  {
    icon: LockOutlinedIcon,
    title: 'Credential handling carries a different level of stakes',
    body: 'Access into core banking, accounting, or client-portal systems gets a level of scrutiny general SMB IT rarely sees — a mishandled credential into a financial client is the kind of thing that gets reported, not just fixed quietly.',
  },
];

export function SolutionsFinancialServices(): JSX.Element {
  useSeo({
    title: 'For MSPs serving financial services clients',
    description:
      'GLBA and PCI-DSS posture tracking, an encrypted credential vault for financial-system access, and vendor-oversight evidence — built by an MSP that dogfoods Nexus on its own practice, not a finance edition bolted onto a generic platform.',
    path: '/solutions/financial-services',
  });

  return (
    <div>
      <PageHero
        eyebrow="For MSPs serving financial services clients"
        title="Community banks, credit unions, and accounting firms answer to more regulators than most clients you serve."
        subtitle="A small financial-services client carries the same GLBA, PCI-DSS, and vendor-oversight expectations a much larger institution does. Nexus tracks that posture as part of the same platform we run our own practice on, not a finance-specific fork."
      >
        <Reveal delay={150}>
          <div className="mt-9 flex justify-center">
            <NotifyForm />
          </div>
        </Reveal>
      </PageHero>

      <section className="py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="mx-auto mb-14 max-w-2xl text-center">
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-nexus-purple-light">
                What&apos;s actually different about a financial-services client
              </span>
              <h2 className="mt-3 font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                Small financial clients still carry big regulatory weight.
              </h2>
              <p className="mt-4 text-white/70">
                The client is small. The list of regulators and auditors who can ask questions about
                it usually isn&apos;t.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {realities.map((r, i) => {
              const Icon = r.icon;
              return (
                <Reveal key={r.title} delay={i * 90}>
                  <div className="h-full rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                    <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light">
                      <Icon fontSize="small" />
                    </div>
                    <h3 className="font-display text-[17px] font-semibold text-nexus-ink">{r.title}</h3>
                    <p className="mt-1.5 text-sm text-white/65">{r.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-white/[0.015] py-16 md:py-24">
        <div className="container-content max-w-[760px]">
          <Reveal>
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-nexus-purple-light">
              What that means inside Nexus
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold text-nexus-ink md:text-[32px]">
              Not a finance edition — the same platform, used against the same weight of rules.
            </h2>
            <ul className="mt-6 flex flex-col gap-3">
              <li className="flex items-start gap-2.5 text-[15px] leading-relaxed text-white/75">
                <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-nexus-purple-light" />
                <span>
                  <Link to="/compliance/glba" className="text-nexus-purple-light no-underline hover:underline">
                    GLBA posture tracking
                  </Link>{' '}
                  is one of the frameworks the compliance module tracks natively — not a request
                  we&apos;d have to build from scratch for a banking or accounting client.
                </span>
              </li>
              <li className="flex items-start gap-2.5 text-[15px] leading-relaxed text-white/75">
                <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-nexus-purple-light" />
                <span>
                  <Link to="/compliance/pci-dss" className="text-nexus-purple-light no-underline hover:underline">
                    PCI-DSS tracking
                  </Link>{' '}
                  covers any client that touches card payments, evidenced by the same patch and
                  access-logging data the rest of the platform already produces.
                </span>
              </li>
              <li className="flex items-start gap-2.5 text-[15px] leading-relaxed text-white/75">
                <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-nexus-purple-light" />
                <span>
                  The{' '}
                  <Link to="/glossary/credential-vault" className="text-nexus-purple-light no-underline hover:underline">
                    credential vault
                  </Link>{' '}
                  handles core-banking and accounting-system access with encryption, scheduled
                  rotation, and a logged record of every reveal or use — the kind of evidence a
                  vendor-oversight questionnaire is actually asking for.
                </span>
              </li>
              <li className="flex items-start gap-2.5 text-[15px] leading-relaxed text-white/75">
                <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-nexus-purple-light" />
                <span>
                  The{' '}
                  <Link to="/platform/security-suite" className="text-nexus-purple-light no-underline hover:underline">
                    security suite
                  </Link>{' '}
                  turns a finding into an owned, SLA-tracked ticket instead of a report sitting
                  unread ahead of a regulatory exam.
                </span>
              </li>
              <li className="flex items-start gap-2.5 text-[15px] leading-relaxed text-white/75">
                <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-nexus-purple-light" />
                <span>
                  <Link to="/platform/compliance-qbr" className="text-nexus-purple-light no-underline hover:underline">
                    Compliance &amp; QBR reporting
                  </Link>{' '}
                  turns GLBA and PCI-DSS posture into a summary a bank examiner conversation or board
                  meeting can actually use.
                </span>
              </li>
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container-content max-w-[760px] text-center">
          <Reveal>
            <Chip
              label="Honest about scope"
              size="small"
              className="mb-4 border border-nexus-purple/30 bg-nexus-purple/[0.14] font-semibold text-nexus-purple-light"
            />
            <h2 className="font-display text-2xl font-bold text-nexus-ink md:text-3xl">
              We&apos;re not pitching a &quot;financial services edition.&quot;
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-white/75">
              Nexus is one platform, not a vertical-specific fork — GLBA and PCI-DSS are two
              frameworks among the several the compliance module tracks, alongside SOC 2, NIST CSF,
              and ISO 27001. The compliance module tracks posture as implemented, partial, or
              planned per control, with evidence attached — it doesn&apos;t certify a client as
              compliant, and no platform honestly can. That determination stays with the client and
              its own examiner or auditor.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="rounded-[28px] border border-nexus-purple/35 bg-gradient-to-br from-[#2a1450] to-[#120a26] p-8 text-center md:p-16">
              <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                Run your financial-services clients on a platform that tracks the posture they
                answer for.
              </h2>
              <p className="mx-auto mt-3 max-w-[520px] text-white/70">
                Join the design-partner cohort — we&apos;ll talk through what a bank, credit union, or
                accounting client actually needs, not a generic pitch.
              </p>
              <div className="mt-8 flex justify-center">
                <NotifyForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
